import React, {Component} from 'react';
import {Link} from 'react-router-dom';
import {withTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Spinner} from '../../Spinner';
import {FaIcon} from '../../FaIcon';
import {YesNoModal} from '../../elements/YesNoModal';
import {ClientMemoryApi} from '../../../api/clientMemoryApi';
import {FactCard} from './FactCard';
import {DocumentRawModal} from './DocumentRawModal';
import {FactEditModal} from './FactEditModal';
import {FactCreateModal} from './FactCreateModal';
import {FactMergeModal} from './FactMergeModal';
import {findSimilar} from './factSimilarity';
import {FactTypeHelp} from './FactTypeHelp';
import {ClientMemoryAutonomyPanel} from './ClientMemoryAutonomyPanel';
import {
    REVIEW_STATUS, ORIGIN, VISIBILITY, FACT_TYPE_ORDER, factTypeLabelKey,
    DURABILITY_NOISE_ORDER, durabilityLabelKey, isLowPriority,
    DETAIL_LEVEL, effectiveDetailLevel,
} from './factConstants';

// Vista "Hechos": cola de curado (PENDING_REVIEW arriba) + biblioteca de hechos aprobados
// agrupados por tipo. Es el corazón del HITL: sólo lo que se aprueba acá entra al contexto
// del agente. Soporta curado por fila, acciones en lote (cola), fusión/supersesión manual
// (biblioteca), carga manual y un hint anti-duplicados.
class MemoryFactsViewClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            facts: [],
            documentsById: {},
            loading: true,
            busyId: null,
            filters: {type: '', origin: '', visibility: '', detail: '', search: ''},
            selectedQueueIds: [],
            selectedLibraryIds: [],
            editTarget: null,
            savingEdit: false,
            // `{doc, quote}` de la fuente que se está mirando («Ver la cita» de una card), o null.
            rawTarget: null,
            deleteTarget: null,
            deleting: false,
            createOpen: false,
            creating: false,
            mergeOpen: false,
            // Hechos que alimentan el FactMergeModal — única fuente del merge, la setean tanto la
            // selección manual de la biblioteca como un grupo sugerido por el dedup asistido.
            mergeCandidates: [],
            merging: false,
            // Grupos de posibles duplicados (dedup asistido). Fail-soft: si el fetch falla queda [].
            suggestions: [],
            dismissingSuggestion: false,
            batchBusy: false,
            expandedNoiseGroups: {},
            discardGroup: null,
            // Confirmación del borrado masivo de la selección de biblioteca (limpieza en lote).
            deleteSelectionOpen: false,
        };
        this.handleApprove = this.handleApprove.bind(this);
        this.handleReject = this.handleReject.bind(this);
        this.handleOpenSource = this.handleOpenSource.bind(this);
        this.handleConfirmCurrent = this.handleConfirmCurrent.bind(this);
        this.handleEditSave = this.handleEditSave.bind(this);
        this.handleToggleVisibility = this.handleToggleVisibility.bind(this);
        this.handleDeleteConfirm = this.handleDeleteConfirm.bind(this);
        this.handleBatchApprove = this.handleBatchApprove.bind(this);
        this.handleBatchReject = this.handleBatchReject.bind(this);
        this.handleBatchDelete = this.handleBatchDelete.bind(this);
        this.handleMerge = this.handleMerge.bind(this);
        this.handleDismissSuggestion = this.handleDismissSuggestion.bind(this);
        this.handleCreate = this.handleCreate.bind(this);
        this.toggleQueueSelect = this.toggleQueueSelect.bind(this);
        this.toggleSelectAllQueue = this.toggleSelectAllQueue.bind(this);
        this.toggleLibrarySelect = this.toggleLibrarySelect.bind(this);
        this.toggleSelectAllLibrary = this.toggleSelectAllLibrary.bind(this);
        this.toggleNoiseGroup = this.toggleNoiseGroup.bind(this);
    }

    componentDidMount() {
        this.fetchAll();
    }

    /**
     * Al cambiar de cliente, el visor abierto se suelta (review C-cita-03).
     *
     * ⚠️ Se resetea SÓLO lo de este frente. Esta vista no se re-fetchea al cambiar `clientId`
     * —sólo tiene `componentDidMount`— y su ruta no lleva `key`, así que una navegación directa de
     * `/client/A/memory/facts` a `/client/B/memory/facts` dejaría los hechos de A en pantalla.
     * Eso es preexistente y de alcance mayor que este modal: agregarle acá un `fetchAll()` sería
     * cambiar el comportamiento de una pantalla que no vine a tocar y que no puedo probar en esta
     * vuelta. Queda reportado aparte; lo que sí no puede pasar es que el visor muestre el crudo de
     * un documento del cliente anterior.
     */
    componentDidUpdate(prevProps) {
        if (prevProps.clientId !== this.props.clientId && this.state.rawTarget) {
            this.setState({rawTarget: null});
        }
    }

    fetchAll() {
        const {t} = this.props;
        const clientId = this.props.clientId;
        this.setState({loading: true});
        Promise.all([
            ClientMemoryApi.listFacts(clientId),
            ClientMemoryApi.listDocuments(clientId),
            // Fail-soft: el dedup asistido es un extra — si su fetch falla, la vista carga igual
            // sin la sección (nada de toasts por esto).
            ClientMemoryApi.listMergeSuggestions(clientId).catch(() => null),
        ]).then(([factsResp, docsResp, suggestionsResp]) => {
            // Los endpoints de listado devuelven el array directo ({facts}/{documents}), sin
            // envoltorio {status} (mismo patrón que useCustomMetrics). Si el array no viene,
            // es un error (400/404/red) -> avisamos y no escondemos data con un [] silencioso.
            if (!factsResp || !Array.isArray(factsResp.facts)) {
                this.setState({loading: false});
                toast.error(t('client_memory.toast.load_error'));
                return;
            }
            const documentsById = {};
            if (docsResp && Array.isArray(docsResp.documents)) {
                docsResp.documents.forEach((d) => {
                    documentsById[d.id] = d;
                });
            }
            const suggestions = (suggestionsResp && Array.isArray(suggestionsResp.suggestions))
                ? suggestionsResp.suggestions : [];
            this.setState({
                facts: factsResp.facts,
                documentsById,
                suggestions,
                loading: false,
                selectedQueueIds: [],
                selectedLibraryIds: [],
            }, () => this.reportPending());
        }).catch(() => {
            this.setState({loading: false});
            toast.error(t('client_memory.toast.load_error'));
        });
    }

    reportPending() {
        if (typeof this.props.onPendingCountChange !== 'function') return;
        // La misma definición que `pendingCount` del store del mazo (sin el ruido probable): las dos
        // pantallas escriben el mismo badge y no pueden decir números distintos (C-memoria-13).
        const count = this.state.facts.filter(
            (f) => f.review_status === REVIEW_STATUS.PENDING && !isLowPriority(f)).length;
        this.props.onPendingCountChange(count);
    }

    replaceFact(updated) {
        this.setState((prev) => ({
            facts: prev.facts.map((f) => (f.id === updated.id ? updated : f)),
        }), () => this.reportPending());
    }

    removeFact(id) {
        this.setState((prev) => ({
            facts: prev.facts.filter((f) => f.id !== id),
        }), () => this.reportPending());
    }

    _patch(fact, payload, successKey) {
        const {t} = this.props;
        this.setState({busyId: fact.id});
        ClientMemoryApi.patchFact(fact.id, {agency_client_id: this.props.clientId, ...payload})
            .then((data) => {
                this.setState({busyId: null});
                if (data && data.status === 'OK') {
                    this.replaceFact(data.data);
                    if (successKey) toast.success(t(successKey));
                } else {
                    toast.error(t('client_memory.toast.error'));
                }
            })
            .catch(() => {
                this.setState({busyId: null});
                toast.error(t('client_memory.toast.error'));
            });
    }

    handleApprove(fact) {
        this._patch(fact, {action: 'approve'}, 'client_memory.toast.approved');
    }

    // «Ver la cita» de una card: abre el visor del crudo posicionado en la cita del hecho.
    handleOpenSource(doc, fact) {
        this.setState({rawTarget: {doc, quote: fact ? fact.source_quote : null}});
    }

    handleReject(fact) {
        this._patch(fact, {action: 'reject'}, 'client_memory.toast.rejected');
    }

    handleConfirmCurrent(fact) {
        // "Sigue vigente": limpia la marca de desactualizado de un hecho que desapareció de su doc.
        this._patch(fact, {action: 'confirm_current'}, 'client_memory.toast.confirmed_current');
    }

    handleEditSave(fact, statement) {
        const {t} = this.props;
        this.setState({savingEdit: true});
        ClientMemoryApi.patchFact(fact.id, {agency_client_id: this.props.clientId, statement})
            .then((data) => {
                this.setState({savingEdit: false});
                if (data && data.status === 'OK') {
                    this.replaceFact(data.data);
                    this.setState({editTarget: null});
                    toast.success(t('client_memory.toast.edited'));
                } else {
                    toast.error(t('client_memory.toast.error'));
                }
            })
            .catch(() => {
                this.setState({savingEdit: false});
                toast.error(t('client_memory.toast.error'));
            });
    }

    handleToggleVisibility(fact, shared) {
        this._patch(
            fact,
            {visibility: shared ? VISIBILITY.SHARED : VISIBILITY.AGENCY_ONLY},
            shared ? 'client_memory.toast.shared' : 'client_memory.toast.unshared',
        );
    }

    handleDeleteConfirm() {
        const {t} = this.props;
        const fact = this.state.deleteTarget;
        if (!fact) return;
        this.setState({deleting: true});
        ClientMemoryApi.deleteFact(fact.id, this.props.clientId)
            .then((data) => {
                this.setState({deleting: false, deleteTarget: null});
                if (data && data.status === 'OK') {
                    this.removeFact(fact.id);
                    toast.success(t('client_memory.toast.deleted'));
                } else {
                    toast.error(t('client_memory.toast.error'));
                }
            })
            .catch(() => {
                this.setState({deleting: false, deleteTarget: null});
                toast.error(t('client_memory.toast.error'));
            });
    }

    // --- selección ---
    toggleQueueSelect(fact) {
        this.setState((prev) => ({
            selectedQueueIds: prev.selectedQueueIds.includes(fact.id)
                ? prev.selectedQueueIds.filter((x) => x !== fact.id)
                : [...prev.selectedQueueIds, fact.id],
        }));
    }

    toggleLibrarySelect(fact) {
        this.setState((prev) => ({
            selectedLibraryIds: prev.selectedLibraryIds.includes(fact.id)
                ? prev.selectedLibraryIds.filter((x) => x !== fact.id)
                : [...prev.selectedLibraryIds, fact.id],
        }));
    }

    // "Seleccionar todo" de la biblioteca: opera sobre lo FILTRADO (lo que el usuario está viendo),
    // no sobre la biblioteca entera — seleccionar en silencio hechos fuera de pantalla y después
    // borrarlos en lote sería una trampa. El label lo dice ("los N visibles").
    toggleSelectAllLibrary(ids) {
        this.setState((prev) => {
            const all = ids.length > 0 && ids.every((id) => prev.selectedLibraryIds.includes(id));
            if (all) {
                return {selectedLibraryIds: prev.selectedLibraryIds.filter((id) => !ids.includes(id))};
            }
            const merged = new Set([...prev.selectedLibraryIds, ...ids]);
            return {selectedLibraryIds: [...merged]};
        });
    }

    // "Seleccionar todos" de la cola: si ya están todos, deselecciona (toggle). Hace descubrible
    // la aprobación/rechazo en lote (los checkboxes por fila pasaban desapercibidos).
    toggleSelectAllQueue(ids) {
        this.setState((prev) => {
            const all = ids.length > 0 && ids.every((id) => prev.selectedQueueIds.includes(id));
            return {selectedQueueIds: all ? [] : [...ids]};
        });
    }

    // --- acciones en lote (cola) ---
    // N PATCHes en paralelo sobre un set de ids (approve/reject) con un único toast (éxito /
    // fallo parcial / error) y refetch al final. Lo usan tanto la barra de selección de la cola
    // "Por revisar" como el "Descartar grupo" de cada familia de "Ruido probable". El backend no
    // tiene un endpoint bulk atómico (los PATCH son idempotentes → un retry/parcial es seguro).
    _batchIds(ids, action) {
        const {t} = this.props;
        if (!ids.length) return;
        this.setState({batchBusy: true});
        Promise.all(ids.map((id) =>
            ClientMemoryApi.patchFact(id, {agency_client_id: this.props.clientId, action})))
            .then((results) => {
                const ok = results.filter((r) => r && r.status === 'OK').length;
                const total = ids.length;
                if (ok === total) {
                    const key = action === 'approve'
                        ? 'client_memory.toast.batch_approved'
                        : 'client_memory.toast.batch_rejected';
                    toast.success(t(key, {count: ok}));
                } else if (ok > 0) {
                    // Fallo parcial: un solo aviso (no success + error encadenados).
                    toast.warning(t('client_memory.toast.batch_partial', {ok, total}));
                } else {
                    toast.error(t('client_memory.toast.error'));
                }
                this.setState({batchBusy: false});
                this.fetchAll();
            })
            .catch(() => {
                this.setState({batchBusy: false});
                toast.error(t('client_memory.toast.error'));
                this.fetchAll();
            });
    }

    _batch(action) {
        this._batchIds(this.state.selectedQueueIds, action);
    }

    handleBatchApprove() {
        this._batch('approve');
    }

    handleBatchReject() {
        this._batch('reject');
    }

    // Borrado masivo de la selección de biblioteca (limpieza en lote — caso Dorfman: barrer la
    // contaminación de a 15 en vez de card por card). N DELETEs en paralelo, mismo patrón que
    // `_batchIds` (el DELETE es soft e idempotente → un retry/parcial es seguro; sin bulk atómico).
    handleBatchDelete() {
        const {t} = this.props;
        const ids = this.state.selectedLibraryIds;
        if (!ids.length) return;
        this.setState({batchBusy: true, deleteSelectionOpen: false});
        Promise.all(ids.map((id) => ClientMemoryApi.deleteFact(id, this.props.clientId)))
            .then((results) => {
                const ok = results.filter((r) => r && r.status === 'OK').length;
                if (ok === ids.length) {
                    toast.success(t('client_memory.toast.batch_deleted', {count: ok}));
                } else if (ok > 0) {
                    toast.warning(t('client_memory.toast.batch_partial', {ok, total: ids.length}));
                } else {
                    toast.error(t('client_memory.toast.error'));
                }
                this.setState({batchBusy: false});
                this.fetchAll();
            })
            .catch(() => {
                this.setState({batchBusy: false});
                toast.error(t('client_memory.toast.error'));
                this.fetchAll();
            });
    }

    toggleNoiseGroup(category) {
        this.setState((prev) => ({
            expandedNoiseGroups: {
                ...prev.expandedNoiseGroups,
                [category]: !prev.expandedNoiseGroups[category],
            },
        }));
    }

    // --- fusión / supersesión manual (biblioteca + dedup asistido) ---
    // Los hechos a fusionar salen SIEMPRE de `mergeCandidates` (los setea la barra de selección
    // de la biblioteca o un grupo sugerido) — así el prefill del dedup no toca la selección.
    handleMerge(survivorId, survivorStatement) {
        const {t} = this.props;
        const survivor = this.state.mergeCandidates.find((f) => f.id === survivorId);
        const losers = this.state.mergeCandidates
            .map((f) => f.id).filter((id) => id !== survivorId);
        if (!survivor || !losers.length) return;

        this.setState({merging: true});
        const clientId = this.props.clientId;
        const ops = [];
        if (survivorStatement && survivorStatement !== survivor.statement) {
            ops.push(ClientMemoryApi.patchFact(survivorId, {agency_client_id: clientId, statement: survivorStatement}));
        }
        losers.forEach((id) => {
            ops.push(ClientMemoryApi.patchFact(id, {agency_client_id: clientId, supersede_by_id: survivorId}));
        });

        Promise.all(ops)
            .then((results) => {
                const allOk = results.every((r) => r && r.status === 'OK');
                this.setState({merging: false, mergeOpen: false, mergeCandidates: [], selectedLibraryIds: []});
                if (allOk) toast.success(t('client_memory.toast.merged', {count: losers.length}));
                else toast.error(t('client_memory.toast.error'));
                this.fetchAll();
            })
            .catch(() => {
                this.setState({merging: false, mergeOpen: false, mergeCandidates: [], selectedLibraryIds: []});
                toast.error(t('client_memory.toast.error'));
                this.fetchAll();
            });
    }

    // "No son duplicados": persiste el descarte del grupo sugerido (no vuelve a aparecer).
    handleDismissSuggestion(group) {
        const {t} = this.props;
        const factIds = group.facts.map((f) => f.id);
        this.setState({dismissingSuggestion: true});
        ClientMemoryApi.dismissMergeSuggestion(this.props.clientId, factIds)
            .then((data) => {
                this.setState({dismissingSuggestion: false});
                if (data && data.status === 'OK') {
                    this.setState((prev) => ({
                        suggestions: prev.suggestions.filter((g) => g !== group),
                    }));
                    toast.success(t('client_memory.dedup.dismissed'));
                } else {
                    toast.error(t('client_memory.toast.error'));
                }
            })
            .catch(() => {
                this.setState({dismissingSuggestion: false});
                toast.error(t('client_memory.toast.error'));
            });
    }

    // --- carga manual ---
    handleCreate(payload) {
        const {t} = this.props;
        this.setState({creating: true});
        ClientMemoryApi.createFact({agency_client_id: this.props.clientId, ...payload})
            .then((data) => {
                this.setState({creating: false});
                if (data && data.status === 'OK') {
                    this.setState((prev) => ({facts: [data.data, ...prev.facts], createOpen: false}),
                        () => this.reportPending());
                    toast.success(t('client_memory.toast.created'));
                } else {
                    toast.error(t('client_memory.toast.error'));
                }
            })
            .catch(() => {
                this.setState({creating: false});
                toast.error(t('client_memory.toast.error'));
            });
    }

    setFilter(key, value) {
        this.setState((prev) => ({filters: {...prev.filters, [key]: value}}));
    }

    filteredLibrary(library) {
        const {type, origin, visibility, detail, search} = this.state.filters;
        const needle = search.trim().toLowerCase();
        return library.filter((f) => {
            if (type && f.fact_type !== type) return false;
            if (origin && f.origin !== origin) return false;
            if (visibility && f.visibility !== visibility) return false;
            // Nivel: null cuenta como GLOBAL (hechos viejos/manuales sin tag no desaparecen de la
            // vista "Globales" — feedback Barbie: lo global es la lente del no-especialista).
            if (detail && effectiveDetailLevel(f) !== detail) return false;
            if (needle && !(f.statement || '').toLowerCase().includes(needle)) return false;
            return true;
        });
    }

    // Cada opción de los selects lleva la cantidad de hechos que matchea (sobre la biblioteca
    // completa, no la filtrada): ayuda a decidir el filtro sin probar a ciegas. El "(N)" se concatena
    // en JS (no interpolación i18next) → sin riesgo del escape de entidades.
    renderFilters(library) {
        const {t} = this.props;
        const {filters} = this.state;
        const countBy = (key) => library.reduce((acc, f) => {
            acc[f[key]] = (acc[f[key]] || 0) + 1;
            return acc;
        }, {});
        const typeCounts = countBy('fact_type');
        const originCounts = countBy('origin');
        const visCounts = countBy('visibility');
        const detailCounts = library.reduce((acc, f) => {
            const level = effectiveDetailLevel(f);
            acc[level] = (acc[level] || 0) + 1;
            return acc;
        }, {});
        const total = library.length;
        const withCount = (label, n) => `${label} (${n || 0})`;
        return (
            <div className="cmem-filters">
                <input
                    type="text"
                    className="form-control cmem-search"
                    placeholder={t('client_memory.filters.search_placeholder')}
                    value={filters.search}
                    onChange={(e) => this.setFilter('search', e.target.value)}
                />
                <select className="form-control" value={filters.type}
                        onChange={(e) => this.setFilter('type', e.target.value)}>
                    <option value="">{withCount(t('client_memory.filters.all_types'), total)}</option>
                    {FACT_TYPE_ORDER.filter((tp) => typeCounts[tp]).map((tp) => (
                        <option key={tp} value={tp}>{withCount(t(factTypeLabelKey(tp)), typeCounts[tp])}</option>
                    ))}
                </select>
                <select className="form-control" value={filters.origin}
                        onChange={(e) => this.setFilter('origin', e.target.value)}>
                    <option value="">{withCount(t('client_memory.filters.all_origins'), total)}</option>
                    <option value={ORIGIN.AI_INFERRED}>
                        {withCount(t('client_memory.origin.ai_inferred'), originCounts[ORIGIN.AI_INFERRED])}
                    </option>
                    <option value={ORIGIN.CLIENT_ASSERTED}>
                        {withCount(t('client_memory.origin.client_asserted'), originCounts[ORIGIN.CLIENT_ASSERTED])}
                    </option>
                </select>
                <select className="form-control" value={filters.visibility}
                        onChange={(e) => this.setFilter('visibility', e.target.value)}>
                    <option value="">{withCount(t('client_memory.filters.all_visibility'), total)}</option>
                    <option value={VISIBILITY.SHARED}>
                        {withCount(t('client_memory.visibility.shared'), visCounts[VISIBILITY.SHARED])}
                    </option>
                    <option value={VISIBILITY.AGENCY_ONLY}>
                        {withCount(t('client_memory.visibility.internal'), visCounts[VISIBILITY.AGENCY_ONLY])}
                    </option>
                </select>
                <select className="form-control" value={filters.detail}
                        onChange={(e) => this.setFilter('detail', e.target.value)}>
                    <option value="">{withCount(t('client_memory.filters.all_detail'), total)}</option>
                    <option value={DETAIL_LEVEL.GLOBAL}>
                        {withCount(t('client_memory.detail_level.global'), detailCounts[DETAIL_LEVEL.GLOBAL])}
                    </option>
                    <option value={DETAIL_LEVEL.PLATFORM}>
                        {withCount(t('client_memory.detail_level.platform'), detailCounts[DETAIL_LEVEL.PLATFORM])}
                    </option>
                </select>
            </div>
        );
    }

    renderQueueBatchBar() {
        const {t} = this.props;
        const count = this.state.selectedQueueIds.length;
        if (count === 0) return null;
        return (
            <div className="cmem-batch-bar">
                <span className="cmem-batch-count">{t('client_memory.batch.selected', {count})}</span>
                <button type="button" className="btn -alt cmem-sm" disabled={this.state.batchBusy}
                        onClick={this.handleBatchApprove}>
                    {t('client_memory.batch.approve')}
                </button>
                <button type="button" className="cmem-link-danger" disabled={this.state.batchBusy}
                        onClick={this.handleBatchReject}>
                    {t('client_memory.batch.reject')}
                </button>
                <button type="button" className="cmem-link"
                        onClick={() => this.setState({selectedQueueIds: []})}>
                    {t('client_memory.batch.clear')}
                </button>
            </div>
        );
    }

    renderLibraryMergeBar() {
        const {t} = this.props;
        const count = this.state.selectedLibraryIds.length;
        if (count === 0) return null;
        return (
            <div className="cmem-batch-bar">
                <span className="cmem-batch-count">{t('client_memory.batch.selected', {count})}</span>
                <button type="button" className="btn -alt cmem-sm" disabled={count < 2}
                        onClick={() => this.setState((prev) => ({
                            mergeOpen: true,
                            mergeCandidates: prev.facts.filter(
                                (f) => prev.selectedLibraryIds.includes(f.id)),
                        }))}>
                    {t('client_memory.merge_modal.title')}
                </button>
                {count < 2 && <span className="cmem-batch-hint">{t('client_memory.batch.merge_hint')}</span>}
                <button type="button" className="cmem-link-danger" disabled={this.state.batchBusy}
                        onClick={() => this.setState({deleteSelectionOpen: true})}>
                    {t('client_memory.batch.delete')}
                </button>
                <button type="button" className="cmem-link"
                        onClick={() => this.setState({selectedLibraryIds: []})}>
                    {t('client_memory.batch.clear')}
                </button>
            </div>
        );
    }

    // Control "seleccionar todo" de una lista con checkboxes. Es un checkbox REAL (no un link de
    // texto perdido en el header) y va alineado con la columna de checkboxes de las cards, que es
    // donde el ojo lo busca. Siempre visible mientras haya algo que seleccionar: esconderlo con un
    // solo ítem lo vuelve un control que aparece y desaparece, y eso se lee como un estado.
    // `indeterminate` no es una prop de React — hay que escribirlo sobre el nodo.
    renderSelectAll(ids, selectedIds, onToggle, labelKey = 'client_memory.batch.select_all') {
        const {t} = this.props;
        if (ids.length === 0) return null;
        const selectedHere = ids.filter((id) => selectedIds.includes(id)).length;
        const allSelected = selectedHere === ids.length;
        return (
            <label className="cmem-select-all-row">
                <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                        if (el) el.indeterminate = selectedHere > 0 && !allSelected;
                    }}
                    onChange={() => onToggle(ids)}
                />
                <span className="cmem-select-all-label">
                    {allSelected
                        ? t('client_memory.batch.deselect_all')
                        : t(labelKey, {count: ids.length})}
                </span>
            </label>
        );
    }

    renderQueue(pending, similarByPending) {
        const {t} = this.props;
        if (pending.length === 0) return null;
        const ids = pending.map((f) => f.id);
        return (
            <div className="cmem-queue">
                <header className="cmem-section-head -queue">
                    <h3>{t('client_memory.queue.title')}</h3>
                    <span className="cmem-count-pill -pending">{pending.length}</span>
                    {/* Qué GANA el usuario aprobando. La pregunta textual del piloto fue «¿esto
                        para qué me sirve? ¿qué afecta que yo le diga aprobar o no?» (card
                        GcSbgD7B): el hint anterior describía el mecanismo —«todavía no entran al
                        contexto del agente»— sin decir la consecuencia. */}
                    <p className="cmem-section-hint">{t('client_memory.queue.hint')}</p>
                </header>
                {this.renderSelectAll(ids, this.state.selectedQueueIds, this.toggleSelectAllQueue)}
                {this.renderQueueBatchBar()}
                {pending.map((fact) => (
                    <FactCard key={fact.id} fact={fact} mode="queue"
                              documentsById={this.state.documentsById} busy={this.state.busyId === fact.id}
                              selectable selected={this.state.selectedQueueIds.includes(fact.id)}
                              onToggleSelect={this.toggleQueueSelect}
                              similar={similarByPending[fact.id]}
                              onApprove={this.handleApprove} onReject={this.handleReject}
                              onEdit={(f) => this.setState({editTarget: f})}
                              onOpenSource={this.handleOpenSource}/>
                ))}
            </div>
        );
    }

    // Cola de "ruido probable": hechos que el triage de durabilidad degradó (review_priority=LOW),
    // agrupados por familia (durability_class) y colapsados. La acción principal por familia es
    // "Descartar grupo" (un click); expandir permite rescatar/editar los que sean valiosos. Vacía
    // si el triage está apagado (nadie es LOW) → la sección no se renderiza.
    renderNoiseQueue(noise) {
        const {t} = this.props;
        if (noise.length === 0) return null;
        const byCategory = {};
        noise.forEach((f) => {
            const cat = DURABILITY_NOISE_ORDER.includes(f.durability_class) ? f.durability_class : 'OTHER';
            (byCategory[cat] = byCategory[cat] || []).push(f);
        });
        const groups = [...DURABILITY_NOISE_ORDER, 'OTHER']
            .map((cat) => ({cat, items: byCategory[cat] || []}))
            .filter((g) => g.items.length > 0);

        return (
            <div className="cmem-noise-queue">
                <header className="cmem-section-head -noise">
                    <h3>{t('client_memory.noise.title')}</h3>
                    <span className="cmem-count-pill -noise">{noise.length}</span>
                    <p className="cmem-section-hint">{t('client_memory.noise.hint')}</p>
                </header>
                {groups.map((group) => {
                    const expanded = !!this.state.expandedNoiseGroups[group.cat];
                    const ids = group.items.map((f) => f.id);
                    return (
                        <div key={group.cat} className="cmem-noise-group">
                            <div className="cmem-noise-group-head">
                                <button type="button" className="cmem-noise-group-toggle"
                                        onClick={() => this.toggleNoiseGroup(group.cat)}>
                                    <FaIcon iconName={expanded ? 'chevron-down' : 'chevron-right'}/>
                                    <span className="cmem-noise-group-title">
                                        {t(durabilityLabelKey(group.cat))}
                                    </span>
                                    <span className="cmem-noise-group-count">{group.items.length}</span>
                                </button>
                                <button type="button" className="cmem-link-danger"
                                        disabled={this.state.batchBusy}
                                        onClick={() => this.setState({discardGroup: {
                                            ids, label: t(durabilityLabelKey(group.cat))}})}>
                                    {t('client_memory.noise.discard_group')}
                                </button>
                            </div>
                            {expanded && group.items.map((fact) => (
                                <FactCard key={fact.id} fact={fact} mode="queue"
                                          documentsById={this.state.documentsById}
                                          busy={this.state.busyId === fact.id}
                                          onApprove={this.handleApprove} onReject={this.handleReject}
                                          onEdit={(f) => this.setState({editTarget: f})}
                                          onOpenSource={this.handleOpenSource}/>
                            ))}
                        </div>
                    );
                })}
            </div>
        );
    }

    // Dedup asistido: grupos de hechos aprobados que se parecen entre sí (similitud de embeddings,
    // computada por el backend). "Fusionar" abre el mismo FactMergeModal prefilled; "No son
    // duplicados" persiste el descarte. Sin sugerencias → la sección no se renderiza.
    renderDedupSuggestions() {
        const {t} = this.props;
        const {suggestions} = this.state;
        if (suggestions.length === 0) return null;
        return (
            <div className="cmem-dedup">
                <header className="cmem-section-head -dedup">
                    <h3>{t('client_memory.dedup.title')}</h3>
                    <span className="cmem-count-pill">{suggestions.length}</span>
                    <p className="cmem-section-hint">{t('client_memory.dedup.hint')}</p>
                </header>
                {suggestions.map((group, idx) => (
                    <div key={`${group.fact_type}-${idx}`} className="cmem-dedup-group">
                        <div className="cmem-dedup-group-head">
                            <span className="cmem-dedup-group-type">
                                {t(factTypeLabelKey(group.fact_type))}
                            </span>
                            <span className="cmem-dedup-group-count">
                                {t('client_memory.dedup.group_count', {count: group.facts.length})}
                            </span>
                        </div>
                        <ul className="cmem-dedup-statements">
                            {group.facts.map((fact) => (
                                <li key={fact.id}>{fact.statement}</li>
                            ))}
                        </ul>
                        <div className="cmem-dedup-actions">
                            <button type="button" className="btn -alt cmem-sm"
                                    onClick={() => this.setState({
                                        mergeOpen: true, mergeCandidates: group.facts})}>
                                {t('client_memory.dedup.merge')}
                            </button>
                            <button type="button" className="cmem-link"
                                    disabled={this.state.dismissingSuggestion}
                                    onClick={() => this.handleDismissSuggestion(group)}>
                                {t('client_memory.dedup.not_duplicates')}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    renderLibrary(library) {
        const {t} = this.props;
        const filtered = this.filteredLibrary(library);
        const grouped = FACT_TYPE_ORDER
            .map((type) => ({type, items: filtered.filter((f) => f.fact_type === type)}))
            .filter((g) => g.items.length > 0);
        return (
            <div className="cmem-library">
                <header className="cmem-section-head -library">
                    <h3>{t('client_memory.library.title')}</h3>
                    {library.length > 0 && <span className="cmem-count-pill">{library.length}</span>}
                    <button type="button" className="btn -negative cmem-sm cmem-add-btn"
                            onClick={() => this.setState({createOpen: true})}>
                        <FaIcon iconName="plus"/> {t('client_memory.action.add')}
                    </button>
                    {/* La contracara del hint de la cola: acá está lo YA confirmado. Sin decirlo,
                        las dos listas se leen igual y se termina revisando la equivocada. */}
                    <p className="cmem-section-hint">{t('client_memory.library.hint')}</p>
                </header>
                {library.length === 0 && (
                    <p className="cmem-empty-inline">{t('client_memory.library.empty')}</p>
                )}
                {library.length > 0 && this.renderFilters(library)}
                {this.renderSelectAll(
                    filtered.map((f) => f.id), this.state.selectedLibraryIds,
                    this.toggleSelectAllLibrary, 'client_memory.batch.select_all_filtered')}
                {library.length > 0 && this.renderLibraryMergeBar()}
                {library.length > 0 && grouped.length === 0 && (
                    <p className="cmem-empty-inline">{t('client_memory.filters.no_results')}</p>
                )}
                {grouped.map((group) => (
                    <div key={group.type} className="cmem-group">
                        <div className="cmem-group-head">
                            {/* Los grupos son los mismos once tipos que los badges, así que llevan
                                la misma explicación — y por el mismo componente.
                                `-below-left` por lo mismo que el badge: con el default el cartel
                                salía a 25px del borde del rótulo y lo tapaba, y a 390px se recortaba
                                15px contra `.app-content` (QA-VIS-knob2-10). */}
                            <FactTypeHelp type={group.type} placement="-below-left"
                                          className="cmem-group-title">
                                {t(factTypeLabelKey(group.type))}
                            </FactTypeHelp>
                            <span className="cmem-group-count">{group.items.length}</span>
                        </div>
                        {group.items.map((fact) => (
                            <FactCard key={fact.id} fact={fact} mode="library"
                                      documentsById={this.state.documentsById}
                                      busy={this.state.busyId === fact.id}
                                      selectable selected={this.state.selectedLibraryIds.includes(fact.id)}
                                      onToggleSelect={this.toggleLibrarySelect}
                                      onOpenSource={this.handleOpenSource}
                                      onEdit={(f) => this.setState({editTarget: f})}
                                      onToggleVisibility={this.handleToggleVisibility}
                                      onConfirmCurrent={this.handleConfirmCurrent}
                                      onDelete={(f) => this.setState({deleteTarget: f})}/>
                        ))}
                    </div>
                ))}
            </div>
        );
    }

    renderEmpty() {
        const {t} = this.props;
        return (
            <div className="cmem-empty">
                <FaIcon iconName="brain" className="cmem-empty-icon"/>
                <h3>{t('client_memory.empty.title')}</h3>
                <p>{t('client_memory.empty.subtitle')}</p>
                <div className="cmem-empty-actions">
                    <Link className="btn -alt cmem-empty-cta"
                          to={`/client/${this.props.clientId}/memory/setup`}>
                        <FaIcon iconName="cloud-arrow-up"/>
                        {t('client_memory.empty.cta')}
                    </Link>
                    <button type="button" className="cmem-link cmem-empty-secondary"
                            onClick={() => this.setState({createOpen: true})}>
                        {t('client_memory.empty.cta_manual')}
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const {t} = this.props;
        if (this.state.loading) {
            return <div className="box-loader"><Spinner color="positive"/></div>;
        }

        const pending = this.state.facts.filter((f) => f.review_status === REVIEW_STATUS.PENDING);
        // La cola se parte en dos: lo que el gate mandó a revisión por sensibilidad/confianza
        // (revisión de verdad) y lo que el triage de durabilidad degradó (ruido probable, LOW).
        const reviewQueue = pending.filter((f) => !isLowPriority(f));
        const noiseQueue = pending.filter((f) => isLowPriority(f));
        const library = this.state.facts.filter(
            (f) => f.review_status === REVIEW_STATUS.APPROVED && f.is_active);

        // Hint anti-duplicados: para cada candidato, el hecho aprobado más parecido (si lo hay).
        // Memo sobre la referencia de `facts`: sólo depende de los hechos, no de filtros/selección,
        // así no se recomputa al tipear en el buscador.
        let similarByPending;
        if (this._similarCache && this._similarCache.facts === this.state.facts) {
            similarByPending = this._similarCache.map;
        } else {
            similarByPending = {};
            pending.forEach((p) => {
                const match = findSimilar(p, library);
                if (match) similarByPending[p.id] = match;
            });
            this._similarCache = {facts: this.state.facts, map: similarByPending};
        }

        const isEmpty = pending.length === 0 && library.length === 0;

        return (
            <div className="cmem-facts">
                {/* El knob va ARRIBA de la cola y fuera del `isEmpty`: sin hechos todavía es
                    cuando más sentido tiene decidir cuántos van a entrar solos. */}
                <ClientMemoryAutonomyPanel clientId={this.props.clientId}
                                           onFactsApproved={() => this.fetchAll()}/>
                {isEmpty ? this.renderEmpty() : (
                    <>
                        {this.renderQueue(reviewQueue, similarByPending)}
                        {this.renderNoiseQueue(noiseQueue)}
                        {this.renderDedupSuggestions()}
                        {this.renderLibrary(library)}
                    </>
                )}

                {/* «Ver la cita» de una card: el mismo visor que la tabla de documentos, posicionado
                    en la cita del hecho. */}
                <DocumentRawModal
                    isOpen={!!this.state.rawTarget}
                    document={this.state.rawTarget ? this.state.rawTarget.doc : null}
                    quote={this.state.rawTarget ? this.state.rawTarget.quote : null}
                    clientId={this.props.clientId}
                    toggle={() => this.setState({rawTarget: null})}
                />
                <FactEditModal
                    isOpen={!!this.state.editTarget}
                    fact={this.state.editTarget}
                    saving={this.state.savingEdit}
                    toggle={() => this.setState({editTarget: null})}
                    onSave={this.handleEditSave}
                />
                <FactCreateModal
                    isOpen={this.state.createOpen}
                    saving={this.state.creating}
                    toggle={() => this.setState({createOpen: false})}
                    onCreate={this.handleCreate}
                />
                <FactMergeModal
                    isOpen={this.state.mergeOpen}
                    facts={this.state.mergeCandidates}
                    saving={this.state.merging}
                    toggle={() => this.setState({mergeOpen: false, mergeCandidates: []})}
                    onMerge={this.handleMerge}
                />
                <YesNoModal
                    skipRenderButton
                    isOpen={!!this.state.deleteTarget}
                    title={t('client_memory.delete_modal.title')}
                    question={t('client_memory.delete_modal.question')}
                    isSaving={this.state.deleting}
                    onYesAnswer={this.handleDeleteConfirm}
                    onNoAnswer={() => this.setState({deleteTarget: null})}
                />
                <YesNoModal
                    skipRenderButton
                    isOpen={this.state.deleteSelectionOpen}
                    title={t('client_memory.batch.delete_confirm_title')}
                    question={t('client_memory.batch.delete_confirm_question', {
                        count: this.state.selectedLibraryIds.length,
                    })}
                    isSaving={this.state.batchBusy}
                    onYesAnswer={this.handleBatchDelete}
                    onNoAnswer={() => this.setState({deleteSelectionOpen: false})}
                />
                <YesNoModal
                    skipRenderButton
                    isOpen={!!this.state.discardGroup}
                    title={t('client_memory.noise.discard_confirm_title')}
                    question={this.state.discardGroup ? t('client_memory.noise.discard_confirm_question', {
                        count: this.state.discardGroup.ids.length,
                        group: this.state.discardGroup.label,
                        interpolation: {escapeValue: false},
                    }) : ''}
                    isSaving={this.state.batchBusy}
                    onYesAnswer={() => {
                        const ids = this.state.discardGroup.ids;
                        this.setState({discardGroup: null});
                        this._batchIds(ids, 'reject');
                    }}
                    onNoAnswer={() => this.setState({discardGroup: null})}
                />
            </div>
        );
    }
}

export const MemoryFactsView = withTranslation()(MemoryFactsViewClass);
